import { useState, useRef, useEffect, type JSX } from 'react'
import {
  Filter,
  MessageSquare,
  Phone,
  Mail,
  Check,
  User,
  Users,
  Inbox,
  Eye } from 'lucide-react'
import { Dialog } from 'primereact/dialog'
import { InputText } from 'primereact/inputtext'
import { InputTextarea } from 'primereact/inputtextarea'
import { Dropdown } from 'primereact/dropdown'
import { Button } from 'primereact/button'
import { useConversationsStore } from '../../store/conversationsStore.js'
import { useConversations } from '../../hooks/useConversations.js'
import { useClickOutside } from '../../hooks/useClickOutside.js'
import { api } from '../../api/client.js'
import type { AssigneeFilter, ChannelOrigin, ConversationStatusFilter } from './types.js'
import { cn } from '../../utils/cn.js'
import { FacebookIcon, WhatsAppIcon, InstagramIcon, YouTubeIcon, XIcon, TelegramIcon } from './BrandIcons.js'

const STATUS_OPTIONS: { label: string; value: ConversationStatusFilter }[] = [
  { label: 'Open', value: 'open' },
  { label: 'Reopened', value: 'reopen' },
  { label: 'On Hold', value: 'on_hold' },
  { label: 'Closed', value: 'closed' },
  { label: 'All', value: 'all' },
]

const CHANNEL_OPTIONS: { label: string; value: ChannelOrigin; icon: React.ComponentType<{ className?: string }> }[] = [
  { label: 'WhatsApp', value: 'whatsapp', icon: WhatsAppIcon },
  { label: 'Telegram', value: 'telegram', icon: TelegramIcon },
  { label: 'Facebook', value: 'facebook', icon: FacebookIcon },
  { label: 'Instagram', value: 'instagram', icon: InstagramIcon },
  { label: 'YouTube', value: 'youtube', icon: YouTubeIcon },
  { label: 'X', value: 'x', icon: XIcon },
  { label: 'SMS', value: 'sms', icon: MessageSquare },
  { label: 'Voice', value: 'voice', icon: Phone },
  { label: 'Email', value: 'email', icon: Mail },
]

const CREATE_CHANNEL_OPTIONS = [
  { label: 'Email', value: 'email' },
  { label: 'SMS', value: 'sms' },
  { label: 'WhatsApp', value: 'whatsapp' },
  { label: 'Facebook', value: 'facebook' },
  { label: 'Instagram', value: 'instagram' },
  { label: 'Chat', value: 'chat' },
  { label: 'Voice', value: 'voice' },
]

const ASSIGNEE_OPTIONS: { label: string; value: AssigneeFilter; icon: typeof User }[] = [
  { label: 'Me + queues', value: 'me-and-queues', icon: Users },
  { label: 'Me only', value: 'me-only', icon: User },
  { label: 'Unassigned', value: 'unassigned', icon: Inbox },
  { label: 'All', value: 'all', icon: Eye },
]

interface ParentConversationOption {
  id: string
  referenceId: string
  subject: string | null
}

interface ConversationSearchResponse {
  items: ParentConversationOption[]
}

function escapeHtml(value: string): string {
  return value
    .replaceAll('&', '&amp;')
    .replaceAll('<', '&lt;')
    .replaceAll('>', '&gt;')
    .replaceAll('"', '&quot;')
    .replaceAll("'", '&#39;')
}

export default function ConversationFilters(): JSX.Element {
  const filters = useConversationsStore((s) => s.filters)
  const setFilters = useConversationsStore((s) => s.setFilters)
  const { refetch } = useConversations()

  const [showCreateDialog, setShowCreateDialog] = useState(false)
  const [showChannelMenu, setShowChannelMenu] = useState(false)
  const [showAssigneeMenu, setShowAssigneeMenu] = useState(false)
  const [createChannel, setCreateChannel] = useState('email')
  const [senderHandle, setSenderHandle] = useState('')
  const [subject, setSubject] = useState('')
  const [sendEmailNow, setSendEmailNow] = useState(false)
  const [initialMessage, setInitialMessage] = useState('')
  const [parentSearch, setParentSearch] = useState('')
  const [parentOptions, setParentOptions] = useState<ParentConversationOption[]>([])
  const [selectedParent, setSelectedParent] = useState<ParentConversationOption | null>(null)
  const [parentSearchLoading, setParentSearchLoading] = useState(false)
  const [createError, setCreateError] = useState('')
  const [createdConversation, setCreatedConversation] = useState<{ id: string; referenceId: string } | null>(null)
  const [loading, setLoading] = useState(false)

  const channelRef = useRef<HTMLDivElement>(null)
  const assigneeRef = useRef<HTMLDivElement>(null)
  useClickOutside(channelRef, () => setShowChannelMenu(false), showChannelMenu)
  useClickOutside(assigneeRef, () => setShowAssigneeMenu(false), showAssigneeMenu)

  // Listen for New button click from ConversationList header
  useEffect(() => {
    function onNew(): void {
      setShowCreateDialog(true)
    }
    window.addEventListener('conversations:new', onNew)
    return () => window.removeEventListener('conversations:new', onNew)
  }, [])

  useEffect(() => {
    const query = parentSearch.trim()
    if (query.length < 2 || selectedParent?.referenceId === query) return
    let cancelled = false
    const timer = setTimeout(() => {
      setParentSearchLoading(true)
      const params = new URLSearchParams({ referenceId: query, limit: '5', offset: '0' })
      void api.get<ConversationSearchResponse>(`/conversations?${params.toString()}`)
        .then((res) => {
          if (!cancelled) setParentOptions(res.items ?? [])
        })
        .catch((err: unknown) => {
          if (!cancelled) {
            setParentOptions([])
            console.error('Failed to search parent conversations:', err)
          }
        })
        .finally(() => {
          if (!cancelled) setParentSearchLoading(false)
        })
    }, 250)
    return () => {
      cancelled = true
      clearTimeout(timer)
    }
  }, [parentSearch, selectedParent])

  function resetCreateForm(): void {
    setSenderHandle('')
    setSubject('')
    setSendEmailNow(false)
    setInitialMessage('')
    setParentSearch('')
    setParentOptions([])
    setParentSearchLoading(false)
    setSelectedParent(null)
    setCreateError('')
    setCreatedConversation(null)
    setCreateChannel('email')
  }

  function closeCreateDialog(): void {
    setShowCreateDialog(false)
    resetCreateForm()
  }

  async function sendInitialEmail(conversationId: string, emailSubject: string): Promise<void> {
    const content = initialMessage.trim()
    const contentHtml = `<p>${escapeHtml(content).replaceAll('\n', '<br>')}</p>`
    await api.post(`/conversations/${conversationId}/messages`, {
      contentText: content,
      contentHtml,
      visibility: 'customer',
      recipientHandle: senderHandle.trim(),
      emailSubject: emailSubject.trim() || 'New conversation',
    })
  }

  async function handleCreateConversation(): Promise<void> {
    if (!senderHandle.trim()) return
    if (createdConversation) {
      setLoading(true)
      setCreateError('')
      try {
        await sendInitialEmail(createdConversation.id, subject)
        closeCreateDialog()
        refetch()
      } catch (err) {
        setCreateError(`Conversation #${createdConversation.referenceId} exists, but the email could not be sent. ${err instanceof Error ? err.message : ''}`.trim())
      } finally {
        setLoading(false)
      }
      return
    }
    if (sendEmailNow && !initialMessage.trim()) {
      setCreateError('Enter a message to send, or clear “Send email now”.')
      return
    }
    if (sendEmailNow && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(senderHandle.trim())) {
      setCreateError('Enter a valid email address to send the first email.')
      return
    }
    setLoading(true)
    setCreateError('')
    try {
      const created = await api.post<{ conversationId: string; referenceId: string }>('/conversations', {
        channel: createChannel,
        senderHandle: senderHandle.trim(),
        subject: subject.trim() || undefined,
        description: initialMessage.trim() || undefined,
        notifyEmail: false,
        ...(selectedParent ? { parentId: selectedParent.id } : {}),
      })
      if (sendEmailNow && createChannel === 'email') {
        setCreatedConversation({ id: created.conversationId, referenceId: created.referenceId })
        try {
          await sendInitialEmail(created.conversationId, subject)
        } catch (err) {
          setCreateError(`Conversation #${created.referenceId} was created, but the email could not be sent. You can retry without creating another conversation. ${err instanceof Error ? err.message : ''}`.trim())
          refetch()
          return
        }
      }
      closeCreateDialog()
      refetch()
    } catch (err) {
      setCreateError(err instanceof Error ? err.message : 'Failed to create conversation')
    } finally {
      setLoading(false)
    }
  }

  const activeChannelLabels = filters.channels.length > 0
    ? filters.channels.map((c) => CHANNEL_OPTIONS.find((o) => o.value === c)?.label ?? c).join(', ')
    : 'All channels'

  const assigneeLabel = ASSIGNEE_OPTIONS.find((o) => o.value === filters.assignee)?.label ?? 'Filter'

  return (
    <>
      {/* Filter bar */}
      <div className="relative bg-white px-3 py-3">
        <div className="flex items-center gap-2">
          {/* Status pills */}
          <div className="flex shrink-0 items-center gap-1 rounded-lg bg-gray-100/70 p-1">
            {STATUS_OPTIONS.map((opt) => (
              <button
                key={opt.value}
                onClick={() => setFilters({ status: opt.value })}
                className={cn(
                  'relative rounded-md px-3 py-1.5 text-[11px] font-medium transition-all',
                  filters.status === opt.value
                    ? 'bg-white text-gray-900 shadow-sm'
                    : 'text-gray-500 hover:text-gray-700',
                )}
              >
                {opt.label}
              </button>
            ))}
          </div>

          {/* Channel filter dropdown */}
          <div ref={channelRef} className="relative shrink-0">
            <button
              onClick={() => setShowChannelMenu((prev: boolean) => !prev)}
              title={activeChannelLabels}
              className={cn(
                'flex h-7 w-7 items-center justify-center rounded-md border text-[10px] font-medium transition-all',
                filters.channels.length > 0
                  ? 'border-blue-200 bg-blue-50 text-blue-700'
                  : 'border-gray-200 bg-white text-gray-500 hover:bg-gray-50',
              )}
            >
              <Filter className="h-3 w-3" />
            </button>
            {showChannelMenu && (
              <div className="absolute left-full top-0 z-30 ml-1 w-44 rounded-xl border border-gray-200 bg-white p-1.5 shadow-lg">
                {/* All channels option */}
                <button
                  onClick={() => {
                    setFilters({ channels: [] })
                    setShowChannelMenu(false)
                  }}
                  className={cn(
                    'flex w-full items-center gap-2 rounded-lg px-2.5 py-1.5 text-xs font-medium transition-colors',
                    filters.channels.length === 0
                      ? 'bg-blue-50 text-blue-700'
                      : 'text-gray-600 hover:bg-gray-50',
                  )}
                >
                  <MessageSquare className="h-3.5 w-3.5" />
                  <span className="flex-1 text-left">All channels</span>
                  {filters.channels.length === 0 && <Check className="h-3.5 w-3.5 text-blue-600" />}
                </button>
                <div className="my-1 border-t border-gray-100" />
                {CHANNEL_OPTIONS.map((opt) => {
                  const active = filters.channels.includes(opt.value)
                  const Icon = opt.icon
                  return (
                    <button
                      key={opt.value}
                      onClick={() => {
                        const next = active
                          ? filters.channels.filter((c) => c !== opt.value)
                          : [...filters.channels, opt.value]
                        setFilters({ channels: next })
                      }}
                      className={cn(
                        'flex w-full items-center gap-2 rounded-lg px-2.5 py-1.5 text-xs font-medium transition-colors',
                        active ? 'bg-blue-50 text-blue-700' : 'text-gray-600 hover:bg-gray-50',
                      )}
                    >
                      <Icon className="h-3.5 w-3.5" />
                      <span className="flex-1 text-left">{opt.label}</span>
                      {active && <Check className="h-3.5 w-3.5 text-blue-600" />}
                    </button>
                  )
                })}
              </div>
            )}
          </div>

          {/* Assignee dropdown */}
          <div ref={assigneeRef} className="relative shrink-0">
            <button
              onClick={() => setShowAssigneeMenu((prev: boolean) => !prev)}
              title={assigneeLabel}
              className={cn(
                'flex h-7 w-7 items-center justify-center rounded-md border text-[10px] font-medium transition-all',
                filters.assignee !== 'all'
                  ? 'border-blue-200 bg-blue-50 text-blue-700'
                  : 'border-gray-200 bg-white text-gray-500 hover:bg-gray-50',
              )}
            >
              <User className="h-3 w-3" />
            </button>
            {showAssigneeMenu && (
              <div className="absolute left-full top-0 z-30 ml-1 w-40 rounded-xl border border-gray-200 bg-white p-1.5 shadow-lg">
                {ASSIGNEE_OPTIONS.map((opt) => {
                  const active = filters.assignee === opt.value
                  const Icon = opt.icon
                  return (
                    <button
                      key={opt.value}
                      onClick={() => {
                        setFilters({ assignee: opt.value })
                        setShowAssigneeMenu(false)
                      }}
                      className={cn(
                        'flex w-full items-center gap-2 rounded-lg px-2.5 py-1.5 text-xs font-medium transition-colors',
                        active ? 'bg-blue-50 text-blue-700' : 'text-gray-600 hover:bg-gray-50',
                      )}
                    >
                      <Icon className="h-3.5 w-3.5" />
                      <span className="flex-1 text-left">{opt.label}</span>
                      {active && <Check className="h-3.5 w-3.5 text-blue-600" />}
                    </button>
                  )
                })}
              </div>
            )}
          </div>

        </div>
      </div>

      <Dialog
        header="Create New Conversation"
        visible={showCreateDialog}
        onHide={() => { if (!loading) closeCreateDialog() }}
        style={{ width: '440px' }}
        modal
      >
        <div className="space-y-4">
          <div>
            <label htmlFor="channel" className="block text-sm font-medium text-gray-700 mb-1">
              Channel
            </label>
            <Dropdown
              id="channel"
              value={createChannel}
              options={CREATE_CHANNEL_OPTIONS}
              onChange={(e: { value: string }) => {
                setCreateChannel(e.value)
                if (e.value !== 'email') setSendEmailNow(false)
              }}
              disabled={!!createdConversation}
              className="w-full"
            />
          </div>
          <div>
            <label htmlFor="senderHandle" className="block text-sm font-medium text-gray-700 mb-1">
              Customer Contact
            </label>
            <InputText
              id="senderHandle"
              value={senderHandle}
              onChange={(e) => setSenderHandle(e.target.value)}
              placeholder="Email, phone number, or handle"
              disabled={!!createdConversation}
              className="w-full"
            />
          </div>
          <div>
            <label htmlFor="subject" className="block text-sm font-medium text-gray-700 mb-1">
              Subject (optional)
            </label>
            <InputText
              id="subject"
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              placeholder="Conversation subject"
              maxLength={998}
              disabled={!!createdConversation}
              className="w-full"
            />
          </div>
          <div>
            <label htmlFor="parentTicket" className="block text-sm font-medium text-gray-700 mb-1">
              Parent ticket (optional)
            </label>
            {selectedParent ? (
              <div className="flex items-center justify-between rounded border border-blue-200 bg-blue-50 px-3 py-2 text-sm">
                <span className="min-w-0 truncate text-blue-900">
                  <strong>#{selectedParent.referenceId}</strong>{selectedParent.subject ? ` · ${selectedParent.subject}` : ''}
                </span>
                {!createdConversation && (
                  <button type="button" className="ml-2 text-blue-700 hover:text-blue-900" onClick={() => {
                    setSelectedParent(null)
                    setParentSearch('')
                  }} aria-label="Remove parent ticket">×</button>
                )}
              </div>
            ) : (
              <>
                <InputText
                  id="parentTicket"
                  value={parentSearch}
                  onChange={(e) => {
                    setParentOptions([])
                    setParentSearchLoading(false)
                    setParentSearch(e.target.value)
                  }}
                  placeholder="Search by ticket reference"
                  disabled={!!createdConversation}
                  className="w-full"
                />
                {parentSearch.trim().length >= 2 && (
                  <div className="mt-1 max-h-36 overflow-y-auto rounded border border-gray-200 bg-white shadow-sm">
                    {parentSearchLoading ? (
                      <div className="px-3 py-2 text-xs text-gray-500">Searching tickets…</div>
                    ) : parentOptions.length > 0 ? parentOptions.map((option) => (
                      <button
                        type="button"
                        key={option.id}
                        onClick={() => {
                          setSelectedParent(option)
                          setParentSearch(option.referenceId)
                          setParentOptions([])
                        }}
                        className="block w-full border-b border-gray-100 px-3 py-2 text-left last:border-0 hover:bg-gray-50"
                      >
                        <span className="block text-xs font-semibold text-gray-800">#{option.referenceId}</span>
                        {option.subject && <span className="block truncate text-xs text-gray-500">{option.subject}</span>}
                      </button>
                    )) : (
                      <div className="px-3 py-2 text-xs text-gray-500">No matching tickets</div>
                    )}
                  </div>
                )}
              </>
            )}
            <p className="mt-1 text-xs text-gray-500">The new conversation will be linked as a child of this ticket.</p>
          </div>
          {createChannel === 'email' && (
            <div className="space-y-2 rounded border border-gray-200 p-3">
              <label className="flex cursor-pointer items-center gap-2 text-sm font-medium text-gray-700">
                <input
                  type="checkbox"
                  checked={sendEmailNow}
                  onChange={(e) => setSendEmailNow(e.target.checked)}
                  disabled={!!createdConversation}
                  className="h-4 w-4 rounded border-gray-300 text-blue-600"
                />
                Send email now
              </label>
              {sendEmailNow && (
                <>
                  <p className="text-xs text-gray-500">Sent to Customer Contact with the subject above and this ticket’s reference.</p>
                  <InputTextarea
                    value={initialMessage}
                    onChange={(e) => setInitialMessage(e.target.value)}
                    placeholder="Write the first email message"
                    rows={5}
                    autoResize
                    disabled={!!createdConversation}
                    className="w-full"
                  />
                </>
              )}
            </div>
          )}
          {createError && <p role="alert" className="text-sm text-red-600">{createError}</p>}
          <div className="flex justify-end gap-2 pt-2">
            <Button
              label="Cancel"
              severity="secondary"
              outlined
              onClick={closeCreateDialog}
              disabled={loading}
            />
            <Button
              label={createdConversation ? 'Retry Email' : sendEmailNow ? 'Create and Send' : 'Create'}
              onClick={handleCreateConversation}
              loading={loading}
              disabled={!senderHandle.trim() || (!!sendEmailNow && !initialMessage.trim())}
            />
          </div>
        </div>
      </Dialog>
    </>
  )
}
